import type { NavigationGuard } from 'vue-router';

import navItems from '@/constants/navItems';
import { getStore } from '@/utilities/storeAccess';
import roles from '@/constants/roles';

const auth: NavigationGuard = async (to, from, next) => {
    const store = getStore();
    if (!store?.getters.isLoggedIn) {
        await store?.dispatch('logout');
        const redirect = encodeURIComponent(to.fullPath);
        next({ path: '/login', query: { redirect }, replace: true });
        return true; // Signal to custom middleware system that next() was called
    }
};
const accessControl: NavigationGuard = (to, from, next) => {
    const store = getStore();
    if (
        to.meta.everyNeededPermissions &&
        !store?.getters.hasEveryPermissions(to.meta.everyNeededPermissions)
    ) {
        next({ path: store?.getters.defaultUserRoute });
        return true;
    } else if (
        to.meta.atLeastNeededRoles &&
        !store?.getters.hasAtLeastOneRole(to.meta.atLeastNeededRoles)
    ) {
        next({ path: store?.getters.defaultUserRoute });
        return true;
    }
    const accessControlLoaded = !!store?.state.accessControl.roles?.length;
    if (to.meta.superAdmin && accessControlLoaded && !store?.getters.superAdmin) {
        next({ path: '/clients' });
        return true;
    }

    const path = to?.path?.split('/')[1];
    if (path) {
        const categoryPath = navItems.find((nav) => nav.name === path);
        const roles = store?.state.accessControl.roles || [];
        if (categoryPath && roles.length) {
            const hasAccess = categoryPath.roles.some((role) => store?.getters.hasRole(role));
            if (!hasAccess) {
                next({ path: store?.getters.defaultUserRoute });
                return true;
            }
        }
    }

    // Load team langs if not already loaded
    if (store?.getters.currentTeamId && !store?.getters.teamLangs.length) {
        store?.dispatch('loadTeamLangs', store?.getters.currentTeamId);
    }
};

const getClientInfo: NavigationGuard = async (to, from) => {
    const store = getStore();
    if (from.params.clientId !== to.params.clientId) {
        await store?.dispatch('getClientInfo', to.params.clientId);
    }
};

const getSiteInfo: NavigationGuard = async (to, from) => {
    const store = getStore();
    if (from.params.siteId !== to.params.siteId) {
        await store?.dispatch('getSiteInfo', to.params.siteId);
    }
};

const isSiteIncludedInClient: NavigationGuard = (to, from, next) => {
    const store = getStore();
    if (from.params.clientId !== to.params.clientId || from.params.siteId !== to.params.siteId) {
        const clientSites: { id: string }[] = store?.getters.client?.sites || [];
        if (clientSites?.length && !clientSites?.find((site) => site.id === to.params.siteId)) {
            next({ path: '/notFound404' });
            return true;
        }
    }
};

const getComponentInfo: NavigationGuard = async (to, from) => {
    const store = getStore();
    if (from.params.componentId !== to.params.componentId) {
        await store?.dispatch('getComponentInfo', to.params.componentId);
    }
};

const getAnalysisTypeInfo: NavigationGuard = async (to, from) => {
    const store = getStore();
    if (from.params.analysisTypeId !== to.params.analysisTypeId) {
        await store?.dispatch('getAnalysisTypeInfo', to.params.analysisTypeId);
    }
};

const isNotOperator: NavigationGuard = (to, from, next) => {
    const store = getStore();
    if (store?.getters.hasRole(roles.ROLE_OPERATOR.value)) {
        next({ path: store.getters.defaultUserRoute });
        return true;
    }
};

const getWorkOrderInfo: NavigationGuard = async (to, from) => {
    const store = getStore();
    if (from.params.workOrderId !== to.params.workOrderId) {
        await store?.dispatch('getWorkOrderInfo', to.params.workOrderId);
    }
};

const decodeURLSignUpData: NavigationGuard = async (to, from, next) => {
    const store = getStore();
    if (to.query.url) {
        const dataValid = await store?.dispatch('decodeUrlInviteData', to.query.url);
        if (dataValid) {
            const canSignup = await store?.dispatch('verifySignupToken', to.query.url);
            if (canSignup) {
                return;
            }
        }
    }
    next({ path: '/login', replace: true });
    return true;
};

const decodeUrlResetPwdData: NavigationGuard = async (to, from, next) => {
    const store = getStore();
    const canResetPwd = await store?.dispatch('decodeUrlResetPwd', to.query.url);
    if (!canResetPwd) {
        next({ path: '/login', replace: true });
        return true;
    }
};

const decodeURLInviteData: NavigationGuard = async (to, from, next) => {
    const store = getStore();
    if (to.query.url) {
        const dataValid = await store?.dispatch('decodeUrlInviteData', to.query.url);
        if (dataValid) {
            return;
        }
    }
    next({ path: '/login', replace: true });
    return true;
};

const dropTokens: NavigationGuard = async () => {
    const store = getStore();
    await store?.dispatch('logout');
};

export default {
    getSiteInfo,
    isSiteIncludedInClient,
    auth,
    accessControl,
    getClientInfo,
    getComponentInfo,
    getAnalysisTypeInfo,
    isNotOperator,
    getWorkOrderInfo,
    dropTokens,
    decodeURLInviteData,
    decodeURLSignUpData,
    decodeUrlResetPwdData,
};
